import { useTranslation } from "react-i18next";
import Chip from "../../../ui/feedback/Chip";
import { termName, useTermCards, type TermCards } from "../../ontology/terms/termNames";
import type { InstanceStatement } from "../../../api/knowledgeData/knowledgeDataDecoders";
import { describeStatement, mostSpecificClasses, type Individual, type StatementDescription, type StatementValue } from "./aboxViewModel";
import TermLink from "./TermLink";

interface StatementSummaryProps {
  readonly statement: InstanceStatement;
  /** `groupIndividuals`' answer for the statements shown together, which names the individuals this one refers to. */
  readonly individuals: readonly Individual[];
  /** The commit of the data model version it was written against, where its terms are named and open. */
  readonly modelCommit: string;
}

/**
 * One statement as a historian reads it (F94, O-1): the class of what it is about and its name,
 * then one row per property under the data model's label. Its words are the text's, shown there,
 * not quoted again; a term opens on the Data model beside (F75).
 */
export default function StatementSummary({ statement, individuals, modelCommit }: StatementSummaryProps) {
  const cards = useTermCards(modelCommit);
  const [main, ...others] = describeStatement(statement, individuals, (iri) => cards?.has(iri) === true);
  return (
    <div className="space-y-1">
      {main === undefined ? null : <Described description={main} cards={cards} commit={modelCommit} />}
      {others.map((description) => (
        <div key={description.individual.iri} className="border-t border-[var(--app-border)] pt-1">
          <Described description={description} cards={cards} commit={modelCommit} />
        </div>
      ))}
      {statement.note === "" ? null : <p className="app-text-muted text-xs">{statement.note}</p>}
    </div>
  );
}

interface Named {
  readonly cards: TermCards | null;
  readonly commit: string;
}

/** What the text calls it: its label, else the words that say it (Daniel's surface form). */
function nameOf(individual: Individual): string | null {
  return individual.name ?? individual.words;
}

/** Its most specific classes, by the data model's parents; all of them until those load. */
function kindsOf(individual: Individual, cards: TermCards | null): string[] {
  return mostSpecificClasses(individual.classes, (iri) => cards?.get(iri)?.parents ?? []);
}

function Described({ description, cards, commit }: Named & { description: StatementDescription }) {
  const { individual, rows } = description;
  // Something described by its properties is named only by a label: its words are the text beside it.
  const name = rows.length === 0 ? nameOf(individual) : individual.name;
  return (
    <>
      <p className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        {kindsOf(individual, cards).map((iri) => <Chip key={iri}><TermLink iri={iri} commit={commit} /></Chip>)}
        {name === null ? null : <span className="app-title font-semibold">{name}</span>}
      </p>
      {rows.length === 0 ? null : (
        <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-3 gap-y-0.5">
          {rows.map((row) => (
            <div key={row.property} className="contents">
              <dt className="app-text-muted"><TermLink iri={row.property} commit={commit} /></dt>
              <dd>{row.values.map((value, index) => <span key={index}>{index === 0 ? null : ", "}<Value value={value} cards={cards} commit={commit} /></span>)}</dd>
            </div>
          ))}
        </dl>
      )}
    </>
  );
}

function Value({ value, cards, commit }: Named & { value: StatementValue }) {
  const { t, i18n } = useTranslation();
  switch (value.kind) {
    case "text":
      return <>{value.text}</>;
    case "term":
      return <TermLink iri={value.iri} commit={commit} />;
    case "individual": {
      const kind = kindsOf(value.individual, cards).map((iri) => termName(iri, cards, i18n.resolvedLanguage ?? "en")).join(", ");
      const name = nameOf(value.individual);
      if (name === null) return <>{kind === "" ? t("sources.document.regest.unnamed") : kind}</>;
      return <>{name}{kind === "" ? null : <span className="app-text-muted text-xs"> {kind}</span>}</>;
    }
  }
}
